import { OrganizationAccessDialog } from "@/components/auth/organization-access-dialog";
import { Outlet, createRootRoute, useRouterState } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { TanStackDevtools } from "@tanstack/react-devtools";
import { useAuth } from "@workos-inc/authkit-react";
import { useAction, useConvexAuth } from "convex/react";
import { useEffect, useState } from "react";
import { api } from "@constructionautomation/backend";

import { AppShell } from "@/components/layout";
import {
  SignInScreen,
  SignInEndpointScreen,
  SIGN_IN_ENDPOINT_PATH,
  AuthLoadingScreen,
  InvitationScreen,
  OrganizationOnboardingScreen,
  UserProfileProvider,
  useUserProfile,
} from "@/components/auth";
import { ProjectProvider } from "@/components/projects";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { Sentry, SentryUserContext, captureUserActionError } from "@/lib/sentry";
import { getInvitationToken } from "@/lib/auth-return-to";

function AuthenticatedApp() {
  const { signOut } = useAuth();
  const { userProfile: viewer, isPending: isViewerPending, error: viewerError } = useUserProfile();
  const syncIdentity = useAction(api.authActions.syncIdentityFromWorkos);
  const [isSyncingIdentity, setIsSyncingIdentity] = useState(false);
  const [hasAttemptedIdentitySync, setHasAttemptedIdentitySync] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  useEffect(() => {
    setHasAttemptedIdentitySync(false);
    setSyncError(null);
  }, []);

  // Also reconcile before showing company setup: an invitation accepted in hosted AuthKit, or a
  // membership webhook that has not arrived yet, must not look like "no company".
  const shouldSyncIdentity =
    viewer !== null &&
    viewer !== undefined &&
    (!viewer.isSynced || !viewer.email || !viewer.name || viewer.requiresOnboarding);

  useEffect(() => {
    if (
      viewer === undefined ||
      isSyncingIdentity ||
      syncError ||
      hasAttemptedIdentitySync
    ) {
      return;
    }
    if (!shouldSyncIdentity) {
      return;
    }

    setIsSyncingIdentity(true);
    setHasAttemptedIdentitySync(true);
    void syncIdentity({})
      .then(() => {
        setSyncError(null);
      })
      .catch((error) => {
        captureUserActionError(error, "auth.syncIdentity", {
          orgId: viewer.organizationId,
        });
        setSyncError(error instanceof Error ? error.message : "Failed to sync user");
      })
      .finally(() => {
        setIsSyncingIdentity(false);
      });
  }, [
    viewer,
    isSyncingIdentity,
    syncIdentity,
    syncError,
    hasAttemptedIdentitySync,
    shouldSyncIdentity,
  ]);

  if (isViewerPending || isSyncingIdentity) {
    return <AuthLoadingScreen />;
  }

  if (viewerError) {
    throw viewerError;
  }

  if (viewer === null) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm rounded-lg border-2 bg-card p-6 text-center">
          <p className="mb-3 text-sm text-destructive">
            Your session has expired. Please sign in again.
          </p>
          <Button
            onClick={() => {
              window.location.reload();
            }}
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (syncError) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm rounded-lg border-2 bg-card p-6 text-center">
          <p className="mb-3 text-sm text-destructive" role="alert">{syncError}</p>
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => {
                setSyncError(null);
                setHasAttemptedIdentitySync(false);
              }}
            >
              Retry
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                void signOut({ navigate: false }).then(() => window.location.reload());
              }}
            >
              Use a Different Account
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // After isViewerPending check, viewer should not be undefined, but TypeScript needs this guard
  if (!viewer) {
    return <AuthLoadingScreen />;
  }

  if (viewer.requiresOnboarding) {
    return <OrganizationOnboardingScreen email={viewer.email} />;
  }

  return (
    <>
      <SentryUserContext viewer={viewer} />
      <ProjectProvider>
        <AppShell>
          <Outlet />
        </AppShell>
      </ProjectProvider>
      <TanStackDevtools
        config={{
          position: "bottom-right",
        }}
        plugins={[
          {
            name: "Tanstack Router",
            render: <TanStackRouterDevtoolsPanel />,
          },
        ]}
      />
    </>
  );
}

function RootComponent() {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });
  // Invitation links work on any path (e.g. the WorkOS sign-in endpoint) and for any auth state.
  const invitationToken = getInvitationToken(searchStr);
  const isInvitationRoute = pathname === "/invite" || invitationToken !== null;
  const [showContent, setShowContent] = useState(false);

  // Delay showing content slightly after auth resolves to allow fade-in
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => setShowContent(true), 50);
      return () => clearTimeout(timer);
    }
    setShowContent(false);
  }, [isLoading]);

  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  if (isInvitationRoute) {
    return <InvitationScreen token={invitationToken} isAuthenticated={isAuthenticated} />;
  }

  if (pathname === SIGN_IN_ENDPOINT_PATH) {
    return <SignInEndpointScreen isAuthenticated={isAuthenticated} />;
  }

  if (!isAuthenticated) {
    return (
      <div
        className={`transition-opacity duration-300 ${showContent ? "opacity-100" : "opacity-0"}`}
      >
        <SignInScreen />
      </div>
    );
  }

  return (
    <div
      className={`h-full transition-opacity duration-300 ${showContent ? "opacity-100" : "opacity-0"}`}
    >
      <UserProfileProvider>
        <AuthenticatedApp />
        <OrganizationAccessDialog />
        <Toaster />
      </UserProfileProvider>
    </div>
  );
}

function RootErrorComponent({ error }: { error: unknown }) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const message =
    error instanceof Error ? error.message : "An unexpected error occurred.";
  const detail =
    import.meta.env.DEV
      ? message
      : "An unexpected error occurred. Please try again.";

  return (
    <div
      className="flex min-h-dvh items-center justify-center bg-background p-4"
      role="alert"
    >
      <div className="w-full max-w-sm rounded-lg border-2 bg-card p-6 text-center">
        <p className="mb-1 text-sm font-medium text-foreground">Something went wrong</p>
        <p className="mb-4 text-sm text-muted-foreground">{detail}</p>
        <Button onClick={() => window.location.reload()}>Reload page</Button>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  errorComponent: RootErrorComponent,
});
